import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Box,
  Divider
} from '@material-ui/core';
import { Person, NavigateNextOutlined, NavigateBeforeOutlined, CloseOutlined, TrendingUp, LocationOn } from '@material-ui/icons';
import AssignmentConfirmationDialog from './AssignmentConfirmationDialog';
import { isEvenWeek } from '../utils/dateUtils';
import moment from 'moment';

// Import our utilities
import { calculateRouteMetrics, processRouteStops } from '../utils/routeDataUtils';
import { formatDuration } from '../utils/routeTimeUtils';
import { useCrewWorkload } from '../hooks/useRouteSchedulingEnhancements';
import { CrewPropType, CrewsPropType, RoutePropType, SitePropType, WeeklyRoutePropType } from '../types/routeTypes';
import { analyzeRouteTimeCalculation, getServiceTimeExplanation } from '../utils/timeCalculations';

// This is now replaced by formatDuration from our utils
const convertMinutesToHours = (mins) => {
  if (!mins) mins = 0;
  let hours = mins / 60;
  hours = Math.round(hours * 100) / 100;
  return hours;
};

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    paddingBottom: '50px',
  },
  colorBar: {
    height: '38px',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
  },
  crewInfo: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: 0,
  },
  crewName: {
    marginLeft: theme.spacing(1),
    color: '#fff',
    fontWeight: 'bold',
  },
  navControls: {
    display: 'flex',
    alignItems: 'center',
  },
  navButton: {
    color: '#fff',
    padding: 8,
  },
  dateInfo: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    justifyContent: 'space-between',
  },
  statsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    padding: theme.spacing(0.5, 0),
    backgroundColor: '#eee',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  compactStatsContainer: {
    padding: 0,
    margin: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  stopsList: {
    flex: 1,
    overflow: 'auto',
    padding: 0,
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
    },
    scrollbarWidth: 'thin',
  },
  compactStopItem: {
    padding: theme.spacing(0.5, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  highlightedStop: {
    backgroundColor: 'rgba(25, 118, 210, 0.08)', // Light blue background
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.24)',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.12)',
    },
  },
  loadingContainer: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1),
  },
  infoItem: {
    padding: theme.spacing(0.5),
    textAlign: 'center',
  },
  infoValue: {
    fontSize: '0.9rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  infoLabel: {
    fontSize: '0.6rem',
    lineHeight: 1,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  footer: {
    position: 'absolute',
    bottom: 5,
    left: 0,
    right: 0,
    padding: theme.spacing(0.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fff',
    zIndex: 2,
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallChip: {
    height: 20,
    fontSize: '0.6rem',
  },
  orderNumber: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    borderRadius: '50%',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
  },
  miniButton: {
    minWidth: 'unset',
    padding: theme.spacing(0, 1),
    fontSize: '0.75rem',
    height: 24,
  },
  stopSecondary: {
    marginTop: 0,
  },
  unassignButton: {
    marginRight: -8,
  },
  // New styles for integrated job impact
  jobImpactContainer: {
    padding: theme.spacing(1),
    backgroundColor: '#f8f9fa',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  jobImpactTitle: {
    fontSize: '0.7rem',
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  jobImpactGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(0.5),
  },
  jobImpactItem: {
    backgroundColor: '#fff',
    borderRadius: 4,
    padding: theme.spacing(0.75),
    border: '1px solid #e0e0e0',
  },
  jobImpactLabel: {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    marginBottom: 2,
  },
  jobImpactValue: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  jobImpactDelta: {
    fontSize: '0.65rem',
    marginLeft: theme.spacing(0.5),
  },
  deltaPositive: {
    color: theme.palette.success.main,
  },
  deltaNegative: {
    color: theme.palette.error.main,
  },
  warningChip: {
    height: 18,
    fontSize: '0.6rem',
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  successChip: {
    height: 18,
    fontSize: '0.6rem',
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  locationIcon: { 
    marginRight: 8, 
    color: theme.palette.primary.main,
    animation: 'bounce 1s infinite'
  },
  locationStuff: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    marginRight: 8,
    animation: 'pulse 2s infinite'
  },
  daySummary: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  summaryItem: {
    textAlign: 'center',
    flex: 1,
  },
  summaryValue: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  summaryLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    marginTop: 2,
  },
  summaryDivider: {
    width: 1,
    height: 40,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 1),
  },
}));

const InfoItem = ({ label, value }) => {
  const classes = useStyles();
  return (
    <div className={classes.infoItem}>
      <Typography className={classes.infoValue}>{value}</Typography>
      <Typography className={classes.infoLabel}>{label}</Typography>
    </div>
  );
};

const SelectedCrewDetails = ({
  crew,
  stops,
  date,
  activeMarker,
  highlightedStop,
  jobSiteDetails,
  handleUnassignment,
  handleAssignToCrew,
  crews,
  markers,
  activeRouteDetails,
  handleClickOnScheduledSite,
  crewRoute,
  weeklyRoutes,
  loading,
  handleChangeCrew,
  closeCrewDetails,
  weeklyHours,
  futureVisits,
  url,
  notification,
  fetchOppositeWeekData
}) => {
  const classes = useStyles();
  const theme = useTheme()

  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [jobImpact, setJobImpact] = useState(null)

  const openAssignmentDialog = () => {
    // Defensive check before opening dialog
    if (!activeMarker) {
      console.error('Cannot open assignment dialog: activeMarker is null');
      return;
    }
    
    // Only check servicesRemaining for scheduled frequencies that require future visits
    const scheduledFrequencies = ['Monthly', 'Quarterly', 'Semi-Annually', 'Annually'];
    if (scheduledFrequencies.includes(activeMarker?.frequency) && !jobSiteDetails?.servicesRemaining?.length) {
      console.error('Cannot open assignment dialog: no services remaining for scheduled frequency job');
      return;
    }
    
    console.log('Opening assignment dialog with:', { activeMarker, jobSiteDetails });
    setIsAssignmentDialogOpen(true);
  };
  
  const closeAssignmentDialog = () => {
    setIsAssignmentDialogOpen(false);
    // Additional safety: if activeMarker became null, ensure dialog is closed
    if (!activeMarker) {
      console.warn('activeMarker became null while dialog was open');
    }
  };

  const handleConfirmAssignment = (assignmentDetails) => {
    // console.log('Confirm assignment', assignmentDetails) 
    handleAssignToCrew(assignmentDetails);
  }
  console.log('SelectedCrewDetails....', date, crew, stops, markers, activeMarker, highlightedStop)
  let crewStops = [];
  const processedSiteIds = new Set(); // Track processed sites to prevent duplicates
  
  // Debug logging
  console.log('SelectedCrewDetails Debug:', {
    crew: crew?._id,
    crewRoute: crewRoute,
    stopsCount: crewRoute?.stops?.length,
    markersCount: markers?.length,
    stopsPropsCount: stops?.length
  });
  
  if (crewRoute?.stops?.length) {
    let crewRouteStops = [];
    crewRoute.stops.forEach((routeStop, index) => {
      // Skip if we've already processed this site
      if (processedSiteIds.has(routeStop.siteId)) {
        return;
      }
      console.log('routeStop...', routeStop)
      // The routeStop might have _id or siteId - need to check both
      const routeStopId = routeStop?._id?.toString() || routeStop?.siteId?.toString()
      
      // First try to find in the stops prop
      let associatedStop = stops?.find(stopItem => {
        console.log('stopItem', stopItem)
        const stopId = stopItem?._id?.toString() || stopItem?.siteId?.toString();
        return stopId === routeStopId || stopId === routeStop.siteId;
      });
      console.log('associatedStop...', associatedStop)
      // If not found in stops, try markers
      if (!associatedStop && markers) {
        associatedStop = markers.find(markerItem => {
          console.log('markerItem', markerItem)
          const markerId = markerItem._id || markerItem.siteId;
          return markerId === routeStopId || markerId === routeStop.siteId;
        });
      }
      console.log('Got it!! associatedStop', associatedStop)
      if (associatedStop) {
        const siteId = associatedStop._id || associatedStop.siteId;
        processedSiteIds.add(routeStopId);
        processedSiteIds.add(siteId);
        
        crewRouteStops.push({
          ...associatedStop,
          ...routeStop, // Ensure route-specific data (like order) is preserved
          orderNumber: index + 1,
          _id: siteId, // Ensure consistent ID
          // Ensure we have all necessary fields
          name: associatedStop.name || routeStop.name || 'Unknown Site',
          address: associatedStop.address || routeStop.address || 'No Address',
          estimatedHours: routeStop.estimatedHours || associatedStop.estimatedHours || 1,
          frequency: associatedStop.frequency || routeStop.frequency || 'Unknown'
        });
      } else {
        // If we can't find associated site details, still show the stop with available data
        console.warn('Using routeStop data directly for:', routeStop);
        crewRouteStops.push({
          ...routeStop,
          orderNumber: index + 1,
          _id: routeStopId,
          name: routeStop.name || `Stop ${index + 1}`,
          address: routeStop.address || 'Location data from route',
          estimatedHours: routeStop.estimatedHours || 1,
          frequency: routeStop.frequency || 'Regular'
        });
      }
    });
    crewStops = crewRouteStops;
  }
  
  crewStops = crewStops.sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
  
  // Add future visits for this crew and date
  if (futureVisits && futureVisits.length > 0) {
    const crewFutureVisits = futureVisits.filter(visit => {
      // Check if this visit is for this crew and this date
      return visit.crew === crew._id && 
             moment(visit.visitDate).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD');
    });
    
    // For each future visit, find the corresponding site details
    crewFutureVisits.forEach(visit => {
      // Skip if we've already processed this site
      if (processedSiteIds.has(visit.siteId)) {
        return;
      }
      
      const site = markers?.find(m => m._id === visit.siteId);
      if (site) {
        processedSiteIds.add(visit.siteId);
        // Add the future visit as a stop with special marking
        crewStops.push({
          ...site,
          ...visit,
          orderNumber: crewStops.length + 1,
          isFutureVisit: true,
          visitType: visit.frequency || site.frequency || 'Future Visit',
          _id: site._id // Ensure consistent ID
        });
      }
    });
  }
  
  // Re-sort with future visits included
  crewStops = crewStops.sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
  
  // Use our route metrics utility
  const crewMetrics = crewRoute ? calculateRouteMetrics(crewRoute) : null;
  let hoursWithoutThisJob = crewMetrics?.totalHours || 0;
  const milesWithoutThisJob = crewMetrics?.milesDriven || 0;
  
  // Add hours from future visits
  const futureVisitHours = crewStops
    .filter(stop => stop.isFutureVisit)
    .reduce((total, stop) => total + (Number(stop.estimatedHours) || 0), 0);
  
  // Add future visit hours to the total
  hoursWithoutThisJob = convertMinutesToHours((hoursWithoutThisJob * 60) + (futureVisitHours * 60));
  
  // Calculate hours with the new job if we have active route details
  let totalHoursWDriveTime = 0;
  if (activeRouteDetails?.totalServiceTime && activeRouteDetails?.totalDriveTime) {
    const totalMinutes = Number(activeRouteDetails.totalServiceTime) + Number(activeRouteDetails.totalDriveTime);
    totalHoursWDriveTime = convertMinutesToHours(totalMinutes);
  }
  const withThisJobHours = totalHoursWDriveTime || 0;
  
  // Use our crew workload hook for weekly calculations
  const crewWorkload = useCrewWorkload(crew._id, weeklyRoutes);
  // Calculate impact of adding this job
  const jobHours = Number(activeMarker?.estimatedHours || 0);
  const withThisJobWeeklyHours = crewWorkload.weeklyTotal + jobHours;
  const withThisJobWeeklyHoursIncludingDriveTime = withThisJobWeeklyHours; // Already includes drive time
  
  const dayOfWeek = moment(date).format('dddd');
  let weekOfTheYear = moment(date).week();
  let evenWeek = isEvenWeek(weekOfTheYear);
  let thisWeek = evenWeek ? 'Even' : 'Odd';

  // Import calculateTravelTime from our route scheduling helpers if needed
  // For now, keeping the simple distance calculation inline
  const calculateSimpleDistance = useCallback((point1, point2) => {
    const lat1 = point1.latitude || point1.location?.coordinates?.[1];
    const lng1 = point1.longitude || point1.location?.coordinates?.[0];
    const lat2 = point2.latitude || point2.location?.coordinates?.[1];
    const lng2 = point2.longitude || point2.location?.coordinates?.[0];
    
    if (!lat1 || !lng1 || !lat2 || !lng2) return 0;
    
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);
  

  const getJobImpact = useCallback(async () => {
    console.log('🚗 === JOB IMPACT CALCULATION ===');
    console.log('🚗 Active marker:', activeMarker?._id, activeMarker?.name);
    console.log('🚗 activeRouteDetails:', activeRouteDetails);
    console.log('🚗 Calculated for job:', activeRouteDetails?.calculatedForSite);
    
    if (!activeMarker?.isUnassigned) return null;

    const currentWeekType = thisWeek;
    const oppositeWeekType = thisWeek === 'Even' ? 'Odd' : 'Even';
    
    // Get current data
    const currentWeekData = weeklyHours[crew._id] || {};
    const currentDayData = currentWeekData[dayOfWeek] || { serviceTime: 0, driveTime: 0 };
    const currentWeekTotal = currentWeekData.total || { serviceTime: 0, driveTime: 0 };
    
    const jobHours = Number(activeMarker.estimatedHours || 0);
    
    // Calculate miles - check if activeRouteDetails is for the current job
    let additionalMiles = 0;
    let isEstimatedMiles = false;
    
    if (activeRouteDetails?.calculatedForSite === activeMarker._id && activeRouteDetails?.totalMilesDriven) {
      // We have calculated route data for this specific job
      const calculatedTotal = activeRouteDetails.totalMilesDriven;
      const originalMiles = activeRouteDetails.originalMiles || milesWithoutThisJob;
      additionalMiles = Math.round((calculatedTotal - originalMiles) * 100) / 100;
      isEstimatedMiles = activeRouteDetails.isEstimate || false;
      
      console.log('🚗 Using calculated route data:', {
        calculatedTotal,
        originalMiles,
        additionalMiles,
        isEstimate: isEstimatedMiles
      });
    } else {
      // Fallback to simple estimation
      console.log('🚗 ⚠️ No route calculation available, using simple estimate');
      
      // Simple estimate: 10 miles round trip for a new stop
      additionalMiles = 10;
      isEstimatedMiles = true;
      
      // If we have nearby stops, try to make a better estimate
      if (stops && stops.length > 0) {
        // Find the closest stop
        let minDistance = Infinity;
        stops.forEach(stop => {
          const distance = calculateSimpleDistance(activeMarker, stop);
          if (distance > 0 && distance < minDistance) {
            minDistance = distance;
          }
        });
        
        if (minDistance < Infinity && minDistance < 50) { // Sanity check
          // Use 2x the distance to nearest stop as a rough estimate
          additionalMiles = Math.round(minDistance * 2 * 10) / 10;
        }
      }
      
      console.log('🚗 Estimated additional miles:', additionalMiles);
    }
    
    console.log('🚗 === MILES DEBUG ===');
    console.log('🚗 Current crew miles:', milesWithoutThisJob);
    console.log('🚗 Additional miles:', additionalMiles);
    console.log('🚗 Is estimate:', isEstimatedMiles);
    
    // Calculate current state
    const currentDayTotal = currentDayData.serviceTime + currentDayData.driveTime;
    const currentWeekTotalHours = currentWeekTotal.serviceTime + currentWeekTotal.driveTime;
    
    // Calculate with job
    const dayWithJob = currentDayTotal + jobHours;
    const weekWithJob = currentWeekTotalHours + jobHours;
    const milesWithJob = (milesWithoutThisJob || 0) + additionalMiles;
    
    console.log('🚗 Miles calculation:');
    console.log('🚗 - Current miles:', milesWithoutThisJob);
    console.log('🚗 - Additional miles:', additionalMiles);
    console.log('🚗 - Total with job:', milesWithJob);
    
    // For weekly/bi-weekly jobs, calculate opposite week impact
    let oppositeWeekImpact = null;
    if (['Weekly', 'Bi-Weekly'].includes(activeMarker.frequency)) {
      console.log('Lets check what the opposite week looks like', date, crew)
      const oppositeWeekResponse = await fetchOppositeWeekData(date, crew._id)
      console.log('got opposite week data..', oppositeWeekResponse)
      
      if (oppositeWeekResponse?.routes?.routes) {
        // Calculate opposite week hours from the fetched data
        const oppositeRoutes = oppositeWeekResponse.routes.routes;
        let oppositeWeekHours = {
          serviceTime: 0,
          driveTime: 0,
          miles: 0
        };
        
        // Find data for the same day of week in opposite week
        const oppositeDay = oppositeRoutes.find(route => route.day === dayOfWeek);
        let oppositeDayHours = { serviceTime: 0, driveTime: 0, miles: 0 };
        
        if (oppositeDay) {
          const crewRouteOpposite = oppositeDay.routes.find(r => r.crewId === crew._id);
          if (crewRouteOpposite) {
            oppositeDayHours = {
              serviceTime: crewRouteOpposite.totalServiceTime / 60,
              driveTime: crewRouteOpposite.totalDriveTime / 60,
              miles: crewRouteOpposite.totalMilesDriven || 0
            };
          }
        }
        
        // Calculate total week hours for opposite week
        oppositeRoutes.forEach(dayRoute => {
          dayRoute.routes.forEach(route => {
            if (route.crewId === crew._id) {
              oppositeWeekHours.serviceTime += route.totalServiceTime / 60;
              oppositeWeekHours.driveTime += route.totalDriveTime / 60;
              oppositeWeekHours.miles += route.totalMilesDriven || 0;
            }
          });
        });
        
        const jobAffectsOppositeWeek = activeMarker.frequency === 'Weekly' || 
          (activeMarker.frequency === 'Bi-Weekly' && 
           ((activeMarker.evenOrOddWeeks === 'Even' && oppositeWeekType === 'Even') ||
            (activeMarker.evenOrOddWeeks === 'Odd' && oppositeWeekType === 'Odd')));
        
        const oppositeDayTotal = oppositeDayHours.serviceTime + oppositeDayHours.driveTime;
        const oppositeWeekTotal = oppositeWeekHours.serviceTime + oppositeWeekHours.driveTime;
        
        oppositeWeekImpact = {
          weekType: oppositeWeekType,
          current: {
            day: Math.round(oppositeDayTotal * 100) / 100,
            week: Math.round(oppositeWeekTotal * 100) / 100,
            miles: Math.round(oppositeWeekHours.miles * 100) / 100
          },
          withJob: {
            day: Math.round((oppositeDayTotal + (jobAffectsOppositeWeek ? jobHours : 0)) * 100) / 100,
            week: Math.round((oppositeWeekTotal + (jobAffectsOppositeWeek ? jobHours : 0)) * 100) / 100,
            miles: Math.round((oppositeWeekHours.miles + (jobAffectsOppositeWeek ? additionalMiles : 0)) * 100) / 100
          },
          delta: {
            day: jobAffectsOppositeWeek ? Math.round(jobHours * 100) / 100 : 0,
            week: jobAffectsOppositeWeek ? Math.round(jobHours * 100) / 100 : 0,
            miles: jobAffectsOppositeWeek ? Math.round(additionalMiles * 100) / 100 : 0
          },
          affectsWeek: jobAffectsOppositeWeek
        };
      }
    }
    
    let jobImpactData = {
      current: {
        day: Math.round(currentDayTotal * 100) / 100,
        week: Math.round(currentWeekTotalHours * 100) / 100,
        miles: Math.round(milesWithoutThisJob * 100) / 100
      },
      withJob: {
        day: Math.round(dayWithJob * 100) / 100,
        week: Math.round(weekWithJob * 100) / 100,
        miles: Math.round(milesWithJob * 100) / 100
      },
      delta: {
        day: Math.round(jobHours * 100) / 100,
        week: Math.round(jobHours * 100) / 100,
        miles: Math.round(additionalMiles * 100) / 100
      },
      oppositeWeek: oppositeWeekImpact,
      warnings: {
        overtime: dayWithJob > 8,
        heavyWeek: weekWithJob > 40
      },
      isEstimatedMiles
    };
    console.log('🚗 Final job impact data:', jobImpactData);
    return jobImpactData;
  }, [activeMarker, activeRouteDetails, crew._id, weeklyHours, dayOfWeek, thisWeek, 
      milesWithoutThisJob, stops, date, fetchOppositeWeekData, calculateSimpleDistance]);
  

  useEffect(() => {
    // Reset jobImpact when activeMarker changes
    setJobImpact(null);
  }, [activeMarker?._id]);

  useEffect(() => {
    let isMounted = true;
    
    const calculateImpact = async () => {
      console.log('287...lets figure out ourjob impact...', activeMarker, crew)
      // ONLY calculate impact if there's an unassigned active marker
      if (activeMarker?.isUnassigned && crew?._id && isMounted) {
        console.log('lets get our job impact...')
        const impact = await getJobImpact();
        if (isMounted && impact) {
          setJobImpact(impact);
        }
      } else {
        // Clear job impact if no unassigned marker
        setJobImpact(null);
      }
    };
    
    calculateImpact();
    
    return () => {
      isMounted = false;
    };
  }, [activeMarker?._id, activeMarker?.isUnassigned, crew?._id, activeRouteDetails, getJobImpact])
  
  // Analyze time calculation if we have a route
  const timeExplanation = crewRoute ? getServiceTimeExplanation(crewRoute) : null;
  if (timeExplanation && crewRoute) {
    analyzeRouteTimeCalculation(crewRoute);
  }
  
  console.log('SelectedCrewDetails Final Data:', {
    crewStops: crewStops,
    crewStopsCount: crewStops.length,
    activeRouteDetails: activeRouteDetails,
    jobImpact: jobImpact,
    timeExplanation
  })
  
  return (
    <Paper className={classes.root} elevation={3}>
      {/* Crew Header with Color */}
      <div className={classes.colorBar} style={{ backgroundColor: crew.color }}>
        <div className={classes.crewInfo}>
          <Person style={{ color: crew.textColor }} />
          <Typography variant="subtitle1" className={classes.crewName} style={{ color: crew.textColor }}>
            {crew.name}
          </Typography>
        </div>
        <div className={classes.navControls}>
          <Tooltip title="Previous Crew">
            <IconButton size="small" onClick={() => handleChangeCrew('prev')} className={classes.navButton}>
              <NavigateBeforeOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Next Crew">
            <IconButton size="small" onClick={() => handleChangeCrew('next')} className={classes.navButton}>
              <NavigateNextOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={closeCrewDetails} className={classes.navButton}>
              <CloseOutlined />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      
      {/* Date Info */}
      <div className={classes.dateInfo}>
        <Typography variant="caption">
          {date ? date.toLocaleDateString() : 'n/a'} ({dayOfWeek} - {thisWeek})
        </Typography>
        <Typography variant="caption">
          {crewStops.length} Stops
        </Typography>
      </div>

      {/* Day Summary */}
      {!loading && crewRoute && (
        <div className={classes.daySummary}>
          <div className={classes.summaryItem}>
            <div className={classes.summaryValue}>
              {crewStops.length}
            </div>
            <div className={classes.summaryLabel}>Stops</div>
          </div>
          
          <div className={classes.summaryDivider} />
          
          <div className={classes.summaryItem}>
            <Tooltip title={timeExplanation ? timeExplanation.explanation : 'Total service time'}>
              <div>
                <div className={classes.summaryValue}>
                  {convertMinutesToHours(crewRoute.totalServiceTime + (futureVisitHours * 60))}h
                </div>
                <div className={classes.summaryLabel}>Service</div>
              </div>
            </Tooltip>
          </div>
          
          <div className={classes.summaryDivider} />
          
          <div className={classes.summaryItem}>
            <div className={classes.summaryValue}>
              {convertMinutesToHours(crewRoute.totalDriveTime)}h
            </div>
            <div className={classes.summaryLabel}>Drive</div>
          </div>
          
          <div className={classes.summaryDivider} />
          
          <div className={classes.summaryItem}>
            <div className={classes.summaryValue}>
              {Math.round(crewRoute.totalMilesDriven || 0)}
            </div>
            <div className={classes.summaryLabel}>Miles</div>
          </div>
          
          <div className={classes.summaryDivider} />
          
          <div className={classes.summaryItem}>
            <Tooltip title={
              <div>
                <div>Service: {convertMinutesToHours(crewRoute.totalServiceTime)}h</div>
                <div>Drive: {convertMinutesToHours(crewRoute.totalDriveTime)}h</div>
                <div>Total: {hoursWithoutThisJob}h</div>
              </div>
            }>
              <div>
                <div className={classes.summaryValue} style={{
                  color: hoursWithoutThisJob > 8 ? theme.palette.error.main : 
                        hoursWithoutThisJob > 7 ? theme.palette.warning.main : 
                        theme.palette.success.main
                }}>
                  {hoursWithoutThisJob}h
                </div>
                <div className={classes.summaryLabel}>Day Total</div>
              </div>
            </Tooltip>
          </div>
        </div>
      )}
      
      {/* Loading State */}
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress style={{ color: crew.color }} size={40} />
        </div>
      ) : (
        <>
          {/* Integrated Job Impact Display */}
          {(jobImpact && jobImpact?.current) && (
            <div className={classes.jobImpactContainer}>
              <Typography className={classes.jobImpactTitle}>
                <TrendingUp fontSize="small" />
                Job Assignment Impact
                {activeMarker?.frequency && (
                  <Chip 
                    label={activeMarker.frequency} 
                    size="small" 
                    className={classes.smallChip}
                  />
                )}
              </Typography>
              
              <div className={classes.jobImpactGrid}>
                {/* Day Impact */}
                <div className={classes.jobImpactItem}>
                  <div className={classes.jobImpactLabel}>Day Hours</div>
                  <div className={classes.jobImpactValue}>
                    {jobImpact.current.day}h → {jobImpact.withJob.day}h
                    <span className={`${classes.jobImpactDelta} ${classes.deltaPositive}`}>
                      (+{jobImpact.delta.day}h)
                    </span>
                  </div>
                  {jobImpact.warnings.overtime && (
                    <Chip 
                      label="Overtime" 
                      className={classes.warningChip}
                    />
                  )}
                </div>
                
                {/* Week Impact */}
                <div className={classes.jobImpactItem}>
                  <div className={classes.jobImpactLabel}>Week Total</div>
                  <div className={classes.jobImpactValue}>
                    {jobImpact.current.week}h → {jobImpact.withJob.week}h
                    <span className={`${classes.jobImpactDelta} ${classes.deltaPositive}`}>
                      (+{jobImpact.delta.week}h)
                    </span>
                  </div>
                  {jobImpact.warnings.heavyWeek && (
                    <Chip 
                      label="Heavy Week" 
                      className={classes.warningChip}
                    />
                  )}
                </div>
                
                {/* Miles Impact */}
                <div className={classes.jobImpactItem}>
                  <div className={classes.jobImpactLabel}>Daily Miles</div>
                  <div className={classes.jobImpactValue}>
                    {jobImpact.current.miles} → {jobImpact.withJob.miles}
                    <span className={`${classes.jobImpactDelta} ${classes.deltaPositive}`}>
                      (+{jobImpact.delta.miles})
                    </span>
                  </div>
                </div>
                
                {/* Opposite Week (for weekly/bi-weekly) */}
                {jobImpact.oppositeWeek && (
                  <div className={classes.jobImpactItem}>
                    <div className={classes.jobImpactLabel}>{jobImpact.oppositeWeek.weekType} Week</div>
                    <div className={classes.jobImpactValue}>
                      {jobImpact.oppositeWeek.current ? (
                        <>
                          Day: {jobImpact.oppositeWeek.current.day}h → {jobImpact.oppositeWeek.withJob.day}h
                          {jobImpact.oppositeWeek.delta.day > 0 && (
                            <span className={`${classes.jobImpactDelta} ${classes.deltaPositive}`}>
                              (+{jobImpact.oppositeWeek.delta.day}h)
                            </span>
                          )}
                          <br />
                          Week: {jobImpact.oppositeWeek.current.week}h → {jobImpact.oppositeWeek.withJob.week}h
                          {jobImpact.oppositeWeek.delta.week > 0 && (
                            <span className={`${classes.jobImpactDelta} ${classes.deltaPositive}`}>
                              (+{jobImpact.oppositeWeek.delta.week}h)
                            </span>
                          )}
                          <br />
                          Miles: {jobImpact.oppositeWeek.current.miles} → {jobImpact.oppositeWeek.withJob.miles}
                          {jobImpact.oppositeWeek.delta.miles > 0 && (
                            <span className={`${classes.jobImpactDelta} ${classes.deltaPositive}`}>
                              (+{jobImpact.oppositeWeek.delta.miles})
                            </span>
                          )}
                        </>
                      ) : (
                        <span>
                          {jobImpact.oppositeWeek.affectsWeek ? 
                            `Will add ~${jobImpact.oppositeWeek.estimatedDelta}h` : 
                            'No impact'}
                        </span>
                      )}
                    </div>
                    {activeMarker.frequency === 'Bi-Weekly' && !jobImpact.oppositeWeek.affectsWeek && (
                      <Chip 
                        label="No change" 
                        className={classes.successChip}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Stops List with Order Numbers */}
          <List className={classes.stopsList} disablePadding>
            {crewStops.length === 0 ? (
              <ListItem className={classes.compactStopItem}>
                <ListItemText
                  primary="No stops scheduled"
                  secondary="This crew has no stops assigned for this day"
                  primaryTypographyProps={{ variant: 'body2', style: { fontStyle: 'italic' } }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ) : (
              crewStops.map((site, index) => (
              <ListItem
                key={index}
                button
                dense
                disableGutters
                onClick={() => {
                  console.log('Clicked on scheduled site:', site);
                  if (handleClickOnScheduledSite) {
                    handleClickOnScheduledSite(site);
                  }
                }}
                className={`${classes.compactStopItem} ${highlightedStop === site._id ? classes.highlightedStop : ''}`}
              >
                <div className={classes.orderNumber}>
                  {site.orderNumber || index + 1}
                </div>
                {highlightedStop === site._id && (
                  <div className={classes.locationStuff} />
                )}
                <ListItemText
                  primary={site.name}
                  secondary={
                    <Box component="span" className={classes.stopSecondary}>
                      {site.address?.length > 25 ? `${site.address.substring(0, 25)}...` : site.address}
                      <span> • {site.estimatedHours}hrs</span>
                      <br />
                      <Chip
                        size="small"
                        className={classes.smallChip}
                        label={`${site.isFutureVisit ? '📅 ' : ''}${site.visitType || site.frequency}${site.frequency === 'Bi-Weekly' ? ` ${site.evenOrOddWeeks}` : ''}`}
                        color={site.isFutureVisit ? "secondary" : "primary"}
                      />
                    </Box>
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ListItemSecondaryAction className={classes.unassignButton}>
                  <Button
                    size="small"
                    variant="outlined"
                    className={classes.miniButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnassignment(site);
                    }}
                  >
                    Un-Assign
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            )))}
          </List>
          
          {/* Footer with Assign Button */}
          {activeMarker?._id && (
            <div className={classes.footer}>
              <Button
                variant="contained"
                color="primary"
                onClick={openAssignmentDialog}
                fullWidth
              >
                Assign Job
              </Button>
            </div>
          )}
        </>
      )}

      {activeMarker && (
        <AssignmentConfirmationDialog
          open={isAssignmentDialogOpen}
          onClose={closeAssignmentDialog}
          crew={crew}
          site={activeMarker}
          selectedDate={date}
          onConfirm={handleConfirmAssignment}
          jobSiteDetails={jobSiteDetails}
          markers={markers}
        />
      )}
    </Paper>
  );
};

// PropTypes for better type safety and documentation
SelectedCrewDetails.propTypes = {
  crew: CrewPropType.isRequired,
  stops: PropTypes.arrayOf(SitePropType),
  date: PropTypes.instanceOf(Date).isRequired,
  activeMarker: SitePropType,
  highlightedStop: PropTypes.string,
  jobSiteDetails: PropTypes.object,
  handleUnassignment: PropTypes.func.isRequired,
  handleAssignToCrew: PropTypes.func.isRequired,
  crews: CrewsPropType,
  markers: PropTypes.array,
  activeRouteDetails: PropTypes.shape({
    totalServiceTime: PropTypes.number,
    totalDriveTime: PropTypes.number,
    totalMilesDriven: PropTypes.number,
    originalMiles: PropTypes.number,
    calculatedForSite: PropTypes.string,
    isEstimate: PropTypes.bool
  }),
  handleClickOnScheduledSite: PropTypes.func,
  crewRoute: RoutePropType,
  weeklyRoutes: PropTypes.arrayOf(WeeklyRoutePropType),
  loading: PropTypes.bool,
  handleChangeCrew: PropTypes.func,
  closeCrewDetails: PropTypes.func,
  weeklyHours: PropTypes.object,
  futureVisits: PropTypes.array,
  url: PropTypes.string.isRequired,
  notification: PropTypes.func.isRequired,
  fetchOppositeWeekData: PropTypes.func.isRequired
};

export default SelectedCrewDetails;
